/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable react/jsx-key, no-nested-ternary */

import { type FC, useMemo, useState, useRef, useEffect, type JSX } from 'react';
import type { FranchiseListSearchItem } from 'interfaces/content/Ranking';
import type { SearchFilter } from 'interfaces/components/SearchFilter';
import { usePagination, useSortBy, useTable } from 'react-table';
import Button from 'components/Globals/Base/Button';
import Dropdown from 'components/Globals/Base/Dropdown';
import Input from 'components/Globals/Base/Input';
import { ContentTitle } from 'components/ContentTitle';
import createColumns from 'components/Ranking/SearchTable/createColumn';
import { substractLastSlash } from 'utils/stringUtils';
import { useRouter } from 'next/router';
import toKebabCase from 'lodash.kebabcase';
import { RANKING_V2_PAGE } from 'constants/testsIds/rankingV2Page';
import { Icon } from 'components/Icons';
import { RestoreColumns, Table, TablePagination, WrapperTable, TablePaginationInfo } from './SearchTableStyles';

export interface RankingSearchTableProps {
  items: FranchiseListSearchItem[];
  sortableFields: SearchFilter[];
  initialRowCount: number;
  updateRowCount: (value: number) => void;
  rankingSlug?: string;
  search?: string;
}

const RankingSearchTable: FC<RankingSearchTableProps> = ({
  items,
  sortableFields,
  initialRowCount,
  updateRowCount,
  rankingSlug,
  search,
}) => {
  const data = useMemo(
    () =>
      items.map(
        (franchiseListItem) =>
          ({
            Name: franchiseListItem.name,
            Order: franchiseListItem.order,
            Rank:
              franchiseListItem.rank > 50 && rankingSlug === 'worlds-most-admired-companies'
                ? '✓'
                : franchiseListItem.rank,
            ...franchiseListItem.data,
            slug: `${franchiseListItem.slug}/`,
          }) as { [key: string]: string | number },
      ),
    [items],
  );
  const isFortuneOrGlobal500 = ['fortune500', 'global500'].includes(rankingSlug);

  const tablePaginationRef = useRef<HTMLDivElement | null>(null);
  const [scrollTable, setScrollTable] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [visibleRowsCount, setVisibleRowsCount] = useState(
    initialRowCount < 500 && isFortuneOrGlobal500 ? 500 : initialRowCount,
  );
  const isSearchPage = useRouter().route.split('/').includes('search');

  const columns = useMemo(() => createColumns(sortableFields, isSearchPage), [sortableFields]);

  function formatPercent(value: string | number): string {
    if (typeof value === 'string') {
      const numberOnly = parseFloat(value.replace(/[^0-9.-]+/g, ''));
      if (Number.isNaN(numberOnly)) return value;
      return `${numberOnly.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
    }

    if (typeof value === 'number') {
      return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
    }

    return '-';
  }

  const useTableInstance = (pageSize: number) =>
    useTable(
      {
        columns,
        data,
        initialState: {
          // @ts-ignore
          pageIndex: 0,
          pageSize,
          sortBy: search
            ? []
            : [
                {
                  desc: false,
                  id: sortableFields.find((el: SearchFilter) => el.title === 'Rank') ? 'Rank' : 'Name',
                },
              ],
        },
        onColumnVisibilityChange: setColumnVisibility,
        state: {
          columnVisibility,
        },
      },
      useSortBy,
      usePagination,
    );

  const tableInstance = useTableInstance(visibleRowsCount);

  const [pageRowCount, setPageRowCount] = useState(tableInstance.page?.length || 0);
  const [isNextPrevClick, setIsNextPrevClick] = useState(false);

  const FortuneAndGlobal500RowOptions = [50, 100, 150, 200, 250, 300, 350, 400, 450, 500];
  const rowOptions = [5, 10, 20, 25, 50, 100];
  const defaultRowOptions =
    rankingSlug === 'fortune500' || rankingSlug === 'global500' ? FortuneAndGlobal500RowOptions : rowOptions;

  // Extract table presentation state
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const {
    getTableProps,
    getTableBodyProps,
    prepareRow,
    setPageSize,
    gotoPage,
    nextPage,
    previousPage,
    headerGroups,
    visibleColumns,
    page,
    pageOptions,
    state: { pageIndex },
    canNextPage,
    canPreviousPage,
  } = tableInstance;

  const handlePageChange = (newValue: string) => {
    const newPage = newValue ? Number(newValue) - 1 : 0;
    gotoPage(newPage);
  };

  const getLastPageElementCount = (): number => {
    const totalElements = data.length;
    const elementsPerPage = visibleRowsCount;

    if (elementsPerPage === 0) return totalElements;
    if (totalElements === 0) return 0;

    const remainder = totalElements % elementsPerPage;
    return remainder === 0 ? elementsPerPage : remainder;
  };

  const tableDropdownOptions = () => {
    const filteredOptions = defaultRowOptions.filter((option) => option <= data.length);
    const rankCounts = data.reduce(
      (acc, item) => {
        const rank = item.Rank;
        acc[rank] = (typeof acc[rank] === 'number' ? acc[rank] : 0) + 1;
        return acc;
      },
      {} as Record<string | number, number>,
    );

    const hasDuplicateRanks = Object.values(rankCounts).some((count) => typeof count === 'number' && count > 1);
    const mustAddLast =
      (data.length <= defaultRowOptions[defaultRowOptions.length - 1] && !filteredOptions.includes(data.length)) ||
      hasDuplicateRanks;

    const lastPageCount = getLastPageElementCount();
    const filteredOptionsAdjusted =
      lastPageCount < 5 && filteredOptions.length > 1 ? filteredOptions.slice(0, -1) : filteredOptions;
    const options = [
      ...filteredOptionsAdjusted.map((option) => ({ label: `${option} Rows`, value: `${option}` })),
      ...(mustAddLast ? [{ label: `All ${data.length} Rows`, value: `${data.length}` }] : []),
    ];

    return options;
  };

  useEffect(() => {
    if (scrollTable && tablePaginationRef.current) {
      tablePaginationRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setScrollTable(false);
    }
  }, [scrollTable]);

  useEffect(() => {
    if (page.length !== pageRowCount) {
      setPageRowCount(page.length);
      if (isNextPrevClick) {
        setScrollTable(true);
      }
    }
    setIsNextPrevClick(false);
  }, [page]);

  return (
    <>
      {visibleColumns.length !== columns.length && (
        <RestoreColumns>
          <ContentTitle
            tag='h3'
            size={20}
          >
            Restore Columns
          </ContentTitle>
          {tableInstance.columns.reduce((acc: JSX.Element[], curr) => {
            if (!curr.isVisible) {
              acc.push(
                <Button
                  className='btn-restore'
                  variant='secondary'
                  onClick={() => {
                    curr.toggleHidden();
                  }}
                >
                  {curr.Header as JSX.Element}
                </Button>,
              );
            }
            return acc;
          }, [])}
        </RestoreColumns>
      )}
      <WrapperTable>
        <Table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, index) => (
                  <th
                    className={column.isSorted ? (column.isSortedDesc ? 'sort-asc' : 'sort-desc') : ''}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    data-cy={`th-${index}`}
                  >
                    <button
                      type='button'
                      className='remove'
                      onClick={(e) => {
                        e.preventDefault();
                        column.toggleHidden();
                      }}
                      onKeyUp={(e) => {
                        e.preventDefault();
                        column.toggleHidden();
                      }}
                      data-cy={`remove-${index}`}
                    >
                      Remove
                      <Icon
                        icon='X'
                        strokeWidth={3}
                        size={14}
                      />
                    </button>
                    <div
                      className='column-title'
                      data-cy={`head-${toKebabCase(column.Header?.toString())}`}
                    >
                      {column.render('Header')}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.length === 0 && (
              <tr data-cy={RANKING_V2_PAGE.FILTER_NO_RESULTS}>
                <td
                  className='no-results'
                  colSpan={headerGroups[0].headers.length}
                >
                  No results found
                </td>
              </tr>
            )}
            {page.length !== 0 &&
              page.map((row) => {
                prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps()}
                    data-cy={RANKING_V2_PAGE.LIST_ROW}
                  >
                    {row.cells.map((cell, idx) => {
                      return (
                        <td
                          {...cell.getCellProps()}
                          className={
                            (idx === 0 && isSearchPage) ||
                            (idx === 0 && sortableFields.find((el: SearchFilter) => el.title === 'Rank'))
                              ? 'ordered-ranking-column'
                              : ''
                          }
                          data-cy={RANKING_V2_PAGE.LIST_CELL}
                        >
                          {['Profits Percent Change', 'Revenue Percent Change'].includes(cell.column.id)
                            ? formatPercent(cell.value)
                            : cell.value
                              ? cell.render('Cell')
                              : '-'}
                        </td>
                      );
                    })}
                    <td>
                      <a
                        href={substractLastSlash(row.original.slug as string)}
                        className='row-link hidden-text'
                        aria-label={`Read more about ${row.values.Name}`}
                      >
                        View More details about {row.values.Name}
                      </a>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </Table>
      </WrapperTable>
      <TablePaginationInfo
        aria-live='polite'
        className='showing-info'
      >
        {data.length === 0 || visibleRowsCount === 0
          ? 'No companies to display'
          : `Showing ${pageIndex * visibleRowsCount + 1} - ${Math.min((pageIndex + 1) * visibleRowsCount, data.length)} of ${data.length} companies`}
      </TablePaginationInfo>
      <TablePagination ref={tablePaginationRef}>
        <div className='previous'>
          <Button
            type='button'
            onClick={() => {
              setIsNextPrevClick(true);
              previousPage();
            }}
            disabled={!canPreviousPage}
            variant='tertiary'
            hasRightArrow={false}
            hasLeftArrow
            dataCy={RANKING_V2_PAGE.FILTER_PREV_PAGE}
          >
            Previous
          </Button>
        </div>
        <div className='page-info'>
          <span className='page-number'>
            Page{' '}
            <Input
              type='number'
              aria-label='Page Number'
              value={pageIndex + 1 || 1}
              size={{ default: 'small' }}
              dataCy={RANKING_V2_PAGE.FILTER_CURRENT_PAGE_INPUT}
              onChange={handlePageChange}
            />{' '}
            of {pageOptions.length}
          </span>
          <span className='page-size'>
            <Dropdown
              placeholder='Number of rows'
              aria-label='Number of rows'
              onOptionClick={(value) => {
                setPageSize(parseInt(value, 10));
                updateRowCount(parseInt(value, 10));
                setScrollTable(true);
                setVisibleRowsCount(parseInt(value, 10));
              }}
              list={tableDropdownOptions()}
              selected={`${visibleRowsCount} Rows`}
              size='small'
              dataCy={RANKING_V2_PAGE.FILTER_ROWS_TO_SHOW}
              className='dropdown'
            />
          </span>
        </div>

        <div className='next'>
          <Button
            type='button'
            onClick={() => {
              setIsNextPrevClick(true);
              nextPage();
            }}
            disabled={!canNextPage}
            variant='tertiary'
            dataCy={RANKING_V2_PAGE.FILTER_NEXT_PAGE}
          >
            Next
          </Button>
        </div>
      </TablePagination>
    </>
  );
};
export default RankingSearchTable;
